/**
 * Cuentas de WhatsApp conectadas a la organizacion activa.
 *
 * La API nunca devuelve los secretos: solo si estan configurados. Ademas, cada
 * numero se puede diagnosticar contra Meta ("Comprobar conexion") para ver que
 * esta bien, que falta por corregir y que datos hay que pegar en Meta para
 * configurar el webhook.
 */
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import type {
  AccountDiagnostic,
  AccountWebhookInfo,
  CreateAccountRequest,
  DiagnosticCheck,
  UpdateAccountRequest,
  WhatsappAccountView,
} from '@shared/api-types.ts';
import type { AccountStatus } from '@domain/types.ts';
import { ApiError, api, errorMessage, isAbortError } from '../api.ts';
import { EMPTY_VALUE, formatDateTime, formatPhone } from '../format.ts';
import type { NoticeKind } from '../hooks.ts';
import { ACCOUNT_STATUS_LABELS } from '../state.ts';
import {
  Chip,
  EmptyState,
  ErrorBanner,
  Field,
  SegmentedControl,
  SkeletonRows,
  Spinner,
} from './Feedback.tsx';
import { Icon } from './Icon.tsx';
import type { IconName } from './Icon.tsx';

type State =
  | { status: 'loading' }
  | { status: 'ready'; accounts: WhatsappAccountView[] }
  | { status: 'error'; message: string };

/** Estado de la comprobacion contra Meta de la fila desplegada. */
type Verification =
  | { status: 'running' }
  | { status: 'ready'; diagnostic: AccountDiagnostic }
  | { status: 'error'; message: string };

/** Datos para Meta, que se piden a la vez que el diagnostico. */
type WebhookInfo =
  | { status: 'loading' }
  | { status: 'ready'; info: AccountWebhookInfo }
  | { status: 'error'; message: string };

type CopyState = 'idle' | 'copied' | 'failed';

/**
 * Que ocupa la fila desplegada de un numero. Nunca se muestran a la vez el
 * diagnostico y el formulario de edicion: `openId` decide la fila y este modo
 * decide su contenido.
 */
type DetailMode = 'diagnostic' | 'edit';

const CHECK_ICONS: Record<DiagnosticCheck['status'], IconName> = {
  ok: 'success',
  warning: 'warning',
  error: 'error',
};

/**
 * Respaldo de copiado: `navigator.clipboard` solo existe en contexto seguro
 * (HTTPS o localhost), asi que en el resto de casos se usa la seleccion clasica.
 */
function fallbackCopy(text: string): boolean {
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '0';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

function copyText(text: string): Promise<boolean> {
  const clipboard: Clipboard | undefined = navigator.clipboard;
  if (clipboard && typeof clipboard.writeText === 'function') {
    return clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => fallbackCopy(text));
  }
  return Promise.resolve(fallbackCopy(text));
}

/** Valor de configuracion con su boton de copiar al portapapeles. */
function CopyField({ label, value }: { label: string; value: string }): ReactElement {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const handleCopy = useCallback((): void => {
    void copyText(value).then((ok) => {
      setCopyState(ok ? 'copied' : 'failed');
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopyState('idle'), 2400);
    });
  }, [value]);

  return (
    <div className="metafield">
      <p className="metafield__label">{label}</p>
      <div className="metafield__row">
        <code className="metafield__value">{value}</code>
        <button
          type="button"
          className="button button--sm"
          onClick={handleCopy}
          title={
            copyState === 'failed'
              ? 'Selecciona el texto y copialo a mano'
              : 'Copiar al portapapeles'
          }
        >
          <Icon name={copyState === 'copied' ? 'check' : 'copy'} />
          {copyState === 'copied' ? 'Copiado' : copyState === 'failed' ? 'Copia manual' : 'Copiar'}
        </button>
      </div>
    </div>
  );
}

/**
 * Resultado de "Comprobar conexion": cabecera con el veredicto, una linea por
 * comprobacion (con el `detail` del backend tal cual) y los datos que hay que
 * pegar en Meta.
 */
function DiagnosticPanel({
  account,
  verification,
  webhook,
  onRetry,
  onClose,
}: {
  account: WhatsappAccountView;
  verification: Verification;
  webhook: WebhookInfo;
  onRetry: () => void;
  onClose: () => void;
}): ReactElement {
  const diagnostic = verification.status === 'ready' ? verification.diagnostic : null;
  const info = webhook.status === 'ready' ? webhook.info : null;

  // El diagnostico ya trae estos dos datos; el endpoint de informacion los
  // adelanta mientras se comprueba (o si la comprobacion falla).
  const webhookUrl = diagnostic?.expectedWebhookUrl ?? info?.url ?? null;
  const verifyToken = diagnostic ? diagnostic.expectedVerifyToken : (info?.verifyToken ?? null);
  const tokenKnown = diagnostic !== null || info !== null;

  return (
    <div className="diag">
      <div className="diag__head">
        <div>
          <p className="diag__title">Comprobacion de {account.name}</p>
          <p className="diag__hint">
            Se consulta a Meta el token, el numero y la configuracion del webhook. Puede tardar unos
            segundos.
          </p>
        </div>
        <div className="diag__actions">
          <button
            type="button"
            className="button button--sm"
            onClick={onRetry}
            disabled={verification.status === 'running'}
          >
            <Icon name="refresh" />
            Comprobar de nuevo
          </button>
          <button type="button" className="button button--ghost button--sm" onClick={onClose}>
            <Icon name="close" />
            Cerrar
          </button>
        </div>
      </div>

      {verification.status === 'running' ? (
        <div className="diag__loading">
          <Spinner label="Comprobando la conexion con WhatsApp. Puede tardar unos segundos..." />
        </div>
      ) : null}

      {verification.status === 'error' ? (
        <ErrorBanner
          message={verification.message}
          onRetry={onRetry}
          retryLabel="Comprobar de nuevo"
        />
      ) : null}

      {diagnostic ? (
        <>
          <div className="diag__summary">
            <Chip tone={diagnostic.ok ? 'success' : 'danger'}>
              <Icon name={diagnostic.ok ? 'success' : 'error'} />
              {diagnostic.ok ? 'Conexion correcta' : 'Hay algo que corregir'}
            </Chip>
          </div>

          <ul className="checklist">
            {diagnostic.checks.map((check) => (
              <li className={`check check--${check.status}`} key={check.id}>
                <Icon name={CHECK_ICONS[check.status]} className="check__icon" />
                <div className="check__body">
                  <p className="check__label">{check.label}</p>
                  <p className="check__detail">{check.detail}</p>
                </div>
              </li>
            ))}
          </ul>

          {diagnostic.number ? (
            <div className="diag__number">
              <p className="diag__subtitle">Datos del numero en Meta</p>
              <dl className="kv">
                <div className="kv__row">
                  <dt>Nombre verificado</dt>
                  <dd>{diagnostic.number.verifiedName ?? EMPTY_VALUE}</dd>
                </div>
                <div className="kv__row">
                  <dt>Telefono</dt>
                  <dd>
                    {diagnostic.number.displayPhoneNumber
                      ? formatPhone(diagnostic.number.displayPhoneNumber)
                      : EMPTY_VALUE}
                  </dd>
                </div>
                <div className="kv__row">
                  <dt>Calidad</dt>
                  <dd>{diagnostic.number.qualityRating ?? EMPTY_VALUE}</dd>
                </div>
              </dl>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="meta-config">
        <p className="meta-config__title">Datos para configurar en Meta</p>
        <p className="meta-config__hint">
          Pega estos valores en Meta for Developers, en la configuracion del webhook de tu
          aplicacion.
        </p>

        {webhookUrl ? (
          <CopyField label="URL del webhook" value={webhookUrl} />
        ) : webhook.status === 'error' ? (
          <p className="meta-config__missing">{webhook.message}</p>
        ) : (
          <p className="meta-config__hint">Cargando la URL del webhook...</p>
        )}

        {verifyToken ? <CopyField label="Token de verificacion" value={verifyToken} /> : null}

        {tokenKnown && !verifyToken ? (
          <p className="meta-config__missing">
            Este numero no tiene token de verificacion propio: Meta no podra verificar el webhook.
            Configuralo al editar la cuenta.
          </p>
        ) : null}
      </div>
    </div>
  );
}

// --- Alta de un numero -------------------------------------------------------

/**
 * Los limites son los mismos que valida `createSchema` en
 * `POST /api/accounts` (`src/api/routes/accounts.ts`), para que el formulario
 * avise antes de gastar una peticion.
 */
const NAME_MIN = 1;
const NAME_MAX = 120;
const NUMBER_ID_MIN = 5;
const NUMBER_ID_MAX = 64;
const PHONE_MAX = 32;
const TOKEN_MIN = 20;
const TOKEN_MAX = 2000;
const APP_SECRET_MIN = 10;
const APP_SECRET_MAX = 200;
const VERIFY_TOKEN_MIN = 6;
const VERIFY_TOKEN_MAX = 200;
const VERIFY_TOKEN_BYTES = 24;
const GRAPH_API_VERSION_PATTERN = /^v\d+\.\d+$/;
/** Version de la Graph API vigente; solo se cambia si Meta la retira. */
const DEFAULT_GRAPH_API_VERSION = 'v26.0';

/** Campos del formulario que el backend puede senalar en `details`. */
type AccountField =
  | 'name'
  | 'phoneNumberId'
  | 'wabaId'
  | 'displayPhoneNumber'
  | 'accessToken'
  | 'appSecret'
  | 'verifyToken'
  | 'graphApiVersion';

type AccountFormValues = {
  name: string;
  phoneNumberId: string;
  wabaId: string;
  displayPhoneNumber: string;
  accessToken: string;
  appSecret: string;
  verifyToken: string;
  graphApiVersion: string;
};

type AccountFieldErrors = Partial<Record<AccountField, string>>;

const EMPTY_ACCOUNT_FORM: AccountFormValues = {
  name: '',
  phoneNumberId: '',
  wabaId: '',
  displayPhoneNumber: '',
  accessToken: '',
  appSecret: '',
  verifyToken: '',
  graphApiVersion: DEFAULT_GRAPH_API_VERSION,
};

const ACCOUNT_FIELDS: readonly AccountField[] = [
  'name',
  'phoneNumberId',
  'wabaId',
  'displayPhoneNumber',
  'accessToken',
  'appSecret',
  'verifyToken',
  'graphApiVersion',
];

/**
 * Token de verificacion aleatorio, en base64url (solo letras, digitos, `-` y
 * `_`): el mismo valor hay que pegarlo luego en Meta. Se descartan los bytes
 * que no caben en un multiplo exacto de 64 para no sesgar el reparto.
 */
function generateVerifyToken(): string {
  const bytes = new Uint8Array(VERIFY_TOKEN_BYTES);
  crypto.getRandomValues(bytes);
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  let token = '';
  for (const byte of bytes) {
    if (byte < 256 - (256 % alphabet.length)) token += alphabet[byte % alphabet.length];
  }
  return token;
}

/**
 * ¿Hay que dar el valor por vacio a efectos de validacion? Un token de acceso
 * pegado con un espacio de mas ocupa 20 caracteres y el backend lo rechazaria;
 * `accessToken` y `appSecret` no se recortan, asi que se comprueba su `trim`.
 */
function isBlank(value: string): boolean {
  return value.trim().length === 0;
}

function requiredText(value: string, label: string): string | null {
  return isBlank(value) ? `${label} es obligatorio.` : null;
}

function lengthText(value: string, label: string, min: number, max: number): string | null {
  if (isBlank(value)) return null;
  const length = value.trim().length;
  if (length < min) return `${label} debe tener al menos ${min} caracteres.`;
  if (length > max) return `${label} no puede superar los ${max} caracteres.`;
  return null;
}

/** Validacion del cliente espejo de la del backend; solo mensajes, sin `alert`. */
function validateAccountForm(values: AccountFormValues): AccountFieldErrors {
  const errors: AccountFieldErrors = {};

  const name = requiredText(values.name, 'El nombre interno') ?? lengthText(values.name, 'El nombre interno', NAME_MIN, NAME_MAX);
  if (name) errors.name = name;

  const phoneNumberId =
    requiredText(values.phoneNumberId, 'El phone number ID') ??
    lengthText(values.phoneNumberId, 'El phone number ID', NUMBER_ID_MIN, NUMBER_ID_MAX);
  if (phoneNumberId) errors.phoneNumberId = phoneNumberId;

  const wabaId =
    requiredText(values.wabaId, 'El WABA ID') ??
    lengthText(values.wabaId, 'El WABA ID', NUMBER_ID_MIN, NUMBER_ID_MAX);
  if (wabaId) errors.wabaId = wabaId;

  if (values.displayPhoneNumber.trim().length > PHONE_MAX) {
    errors.displayPhoneNumber = `El numero visible no puede superar los ${PHONE_MAX} caracteres.`;
  }

  const accessToken =
    requiredText(values.accessToken, 'El token de acceso') ??
    lengthText(values.accessToken, 'El token de acceso', TOKEN_MIN, TOKEN_MAX);
  if (accessToken) errors.accessToken = accessToken;

  if (isBlank(values.appSecret)) {
    if (values.appSecret.length > 0) {
      errors.appSecret = 'El app secret no puede ser solo espacios; dejalo vacio si no lo tienes.';
    }
  } else if (
    values.appSecret.length < APP_SECRET_MIN ||
    values.appSecret.length > APP_SECRET_MAX
  ) {
    errors.appSecret = `El app secret debe tener entre ${APP_SECRET_MIN} y ${APP_SECRET_MAX} caracteres.`;
  }

  if (values.verifyToken.trim().length > 0) {
    const length = values.verifyToken.trim().length;
    if (length < VERIFY_TOKEN_MIN || length > VERIFY_TOKEN_MAX) {
      errors.verifyToken = `El token de verificacion debe tener entre ${VERIFY_TOKEN_MIN} y ${VERIFY_TOKEN_MAX} caracteres.`;
    }
  }

  if (!GRAPH_API_VERSION_PATTERN.test(values.graphApiVersion.trim())) {
    errors.graphApiVersion = 'La version debe tener el formato v26.0 (una «v», numeros y un punto).';
  }

  return errors;
}

/** Cuerpo de la peticion: los opcionales solo viajan si traen valor. */
function toCreateAccountRequest(values: AccountFormValues): CreateAccountRequest {
  const body: CreateAccountRequest = {
    name: values.name.trim(),
    phoneNumberId: values.phoneNumberId.trim(),
    wabaId: values.wabaId.trim(),
    accessToken: values.accessToken,
    graphApiVersion: values.graphApiVersion.trim(),
  };
  const displayPhoneNumber = values.displayPhoneNumber.trim();
  const appSecret = values.appSecret.trim();
  const verifyToken = values.verifyToken.trim();
  if (displayPhoneNumber) body.displayPhoneNumber = displayPhoneNumber;
  if (appSecret) body.appSecret = appSecret;
  if (verifyToken) body.verifyToken = verifyToken;
  return body;
}

/**
 * Traduce el `details` de un 400 `invalid_body` a errores por campo. Los campos
 * desconocidos y los del formulario entero (`_form`) caen en el mensaje general.
 */
function invalidFieldErrors(details: Record<string, string[]>): {
  fields: AccountFieldErrors;
  general: string | null;
} {
  const fields: AccountFieldErrors = {};
  const general: string[] = [];

  for (const [key, messages] of Object.entries(details)) {
    const message = messages.join(' ');
    if ((ACCOUNT_FIELDS as readonly string[]).includes(key)) {
      fields[key as AccountField] = message;
    } else {
      general.push(message);
    }
  }

  return { fields, general: general.length > 0 ? general.join(' ') : null };
}

/**
 * Formulario de alta de un numero, pensado para quien nunca ha configurado
 * WhatsApp Business: cada campo explica de donde sale su valor.
 */
function CreateAccountForm({
  canWrite,
  onCreated,
  onCancel,
}: {
  canWrite: boolean;
  onCreated: (account: WhatsappAccountView) => void;
  onCancel: () => void;
}): ReactElement {
  const [values, setValues] = useState<AccountFormValues>(EMPTY_ACCOUNT_FORM);
  const [fieldErrors, setFieldErrors] = useState<AccountFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [showAppSecret, setShowAppSecret] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  const copyTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
    },
    [],
  );

  const update = useCallback((field: AccountField, value: string): void => {
    setValues((prev) => ({ ...prev, [field]: value }));
    // Al corregir un campo se retira su aviso, para no dejar errores ya resueltos.
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleGenerateToken = useCallback((): void => {
    const token = generateVerifyToken();
    setValues((prev) => ({ ...prev, verifyToken: token }));
    setFieldErrors((prev) => {
      if (!prev.verifyToken) return prev;
      const next = { ...prev };
      delete next.verifyToken;
      return next;
    });
  }, []);

  const handleCopyToken = useCallback((): void => {
    void copyText(values.verifyToken).then((ok) => {
      if (!ok) return;
      setCopiedToken(true);
      if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopiedToken(false), 2400);
    });
  }, [values.verifyToken]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      const errors = validateAccountForm(values);
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setFormError('Revisa los campos marcados.');
        return;
      }

      setFieldErrors({});
      setFormError(null);
      setSubmitting(true);

      api
        .createAccount(toCreateAccountRequest(values))
        .then((created) => {
          setSubmitting(false);
          onCreated(created);
        })
        .catch((error: unknown) => {
          setSubmitting(false);
          if (error instanceof ApiError) {
            if (error.status === 409) {
              setFormError(
                'Ya hay un numero dado de alta con ese phone number ID. Comprueba que no lo hayas anadido antes (aqui o en otra organizacion).',
              );
              return;
            }
            if (error.status === 400 && error.invalidFields) {
              const parsed = invalidFieldErrors(error.invalidFields);
              setFieldErrors(parsed.fields);
              setFormError(parsed.general ?? 'El servidor ha rechazado algunos campos.');
              return;
            }
          }
          setFormError(errorMessage(error));
        });
    },
    [onCreated, values],
  );

  return (
    <section className="card">
      <div className="card__row">
        <div>
          <h2 className="card__title">Nuevo numero de WhatsApp</h2>
          <p className="card__hint">
            Los datos estan en Meta for Developers dentro de tu app: los dos identificadores y el
            token, en WhatsApp → Configuracion de la API; el app secret, en Configuracion basica.
          </p>
        </div>
        <a
          className="button button--ghost button--sm"
          href="https://developers.facebook.com/apps"
          target="_blank"
          rel="noreferrer"
        >
          <Icon name="panel" />
          Abrir Meta for Developers
        </a>
      </div>

      {!canWrite ? (
        <div className="banner banner--warning" role="status">
          <Icon name="warning" className="banner__icon" />
          <p className="banner__text">
            Solo el propietario y los administradores pueden dar de alta numeros. Pide a una persona
            administradora de la organizacion que lo configure.
          </p>
        </div>
      ) : null}

      <div className="banner banner--warning" role="alert">
        <Icon name="warning" className="banner__icon" />
        <p className="banner__text">
          El token de acceso y el app secret se guardan cifrados en el servidor y no se vuelven a
          mostrar: si los pierdes, hay que generar unos nuevos en Meta. Revisa que el numero sea
          correcto antes de guardarlo.
        </p>
      </div>

      <fieldset className="fieldset" disabled={!canWrite || submitting}>
        <form onSubmit={handleSubmit} noValidate>
          <Field
            label="Nombre interno"
            htmlFor="account-name"
            required
            hint="Solo para el panel: por ejemplo «Atencion al cliente» (1 a 120 caracteres)."
          >
            <input
              id="account-name"
              className="input"
              maxLength={NAME_MAX}
              value={values.name}
              placeholder="Atencion al cliente"
              onChange={(event) => update('name', event.target.value)}
            />
            {fieldErrors.name ? <p className="field__error">{fieldErrors.name}</p> : null}
          </Field>

          <div className="grid grid--2">
            <Field
              label="Phone number ID"
              htmlFor="account-phone-number-id"
              required
              hint="Meta for Developers → tu app → WhatsApp → Configuracion de la API. Copia el «Identificador del numero de telefono» (5 a 64 caracteres)."
            >
              <input
                id="account-phone-number-id"
                className="input input--code"
                maxLength={NUMBER_ID_MAX}
                value={values.phoneNumberId}
                placeholder="123456789012345"
                onChange={(event) => update('phoneNumberId', event.target.value)}
              />
              {fieldErrors.phoneNumberId ? (
                <p className="field__error">{fieldErrors.phoneNumberId}</p>
              ) : null}
            </Field>

            <Field
              label="WABA ID"
              htmlFor="account-waba-id"
              required
              hint="En la misma pantalla: es el «Identificador de la cuenta de WhatsApp Business» de la que cuelga el numero (5 a 64 caracteres)."
            >
              <input
                id="account-waba-id"
                className="input input--code"
                maxLength={NUMBER_ID_MAX}
                value={values.wabaId}
                placeholder="987654321098765"
                onChange={(event) => update('wabaId', event.target.value)}
              />
              {fieldErrors.wabaId ? (
                <p className="field__error">{fieldErrors.wabaId}</p>
              ) : null}
            </Field>
          </div>

          <Field
            label="Numero visible"
            htmlFor="account-display-phone-number"
            hint="Como se ve el numero en WhatsApp, por ejemplo +34 600 000 000. Es opcional; si lo dejas vacio, el panel lo pedira a Meta al comprobar la conexion (maximo 32 caracteres)."
          >
            <input
              id="account-display-phone-number"
              className="input"
              maxLength={PHONE_MAX}
              value={values.displayPhoneNumber}
              placeholder="+34 600 000 000"
              onChange={(event) => update('displayPhoneNumber', event.target.value)}
            />
            {fieldErrors.displayPhoneNumber ? (
              <p className="field__error">{fieldErrors.displayPhoneNumber}</p>
            ) : null}
          </Field>

          <Field
            label="Token de acceso"
            htmlFor="account-access-token"
            required
            hint="El «Token de acceso temporal» de la pantalla de la API, o uno permanente de un usuario del sistema. Empieza por EAA y es largo (20 a 2000 caracteres)."
          >
            <div className="field-row">
              <input
                id="account-access-token"
                className="input input--code"
                type={showAccessToken ? 'text' : 'password'}
                maxLength={TOKEN_MAX}
                value={values.accessToken}
                autoComplete="off"
                spellCheck={false}
                placeholder="EAA..."
                onChange={(event) => update('accessToken', event.target.value)}
              />
              <button
                type="button"
                className="button button--ghost button--sm"
                aria-pressed={showAccessToken}
                onClick={() => setShowAccessToken((visible) => !visible)}
              >
                <Icon name={showAccessToken ? 'close' : 'search'} />
                {showAccessToken ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
            {fieldErrors.accessToken ? (
              <p className="field__error">{fieldErrors.accessToken}</p>
            ) : null}
          </Field>

          <Field
            label="App secret"
            htmlFor="account-app-secret"
            hint="Meta for Developers → tu app → Configuracion basica → «Clave secreta de la app». Opcional: sin el, el panel no podra comprobar la firma de los webhooks (10 a 200 caracteres)."
          >
            <div className="field-row">
              <input
                id="account-app-secret"
                className="input input--code"
                type={showAppSecret ? 'text' : 'password'}
                maxLength={APP_SECRET_MAX}
                value={values.appSecret}
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => update('appSecret', event.target.value)}
              />
              <button
                type="button"
                className="button button--ghost button--sm"
                aria-pressed={showAppSecret}
                onClick={() => setShowAppSecret((visible) => !visible)}
              >
                <Icon name={showAppSecret ? 'close' : 'search'} />
                {showAppSecret ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
            {fieldErrors.appSecret ? (
              <p className="field__error">{fieldErrors.appSecret}</p>
            ) : null}
          </Field>

          <Field
            label="Token de verificacion"
            htmlFor="account-verify-token"
            hint="Lo inventas tu y sirve para que Meta confirme que el webhook es tuyo. Genera uno y pega despues ese mismo valor en Meta, en la configuracion del webhook de tu app (6 a 200 caracteres)."
          >
            <div className="field-row">
              <input
                id="account-verify-token"
                className="input input--code"
                maxLength={VERIFY_TOKEN_MAX}
                value={values.verifyToken}
                autoComplete="off"
                spellCheck={false}
                placeholder="Pulsa «Generar»"
                onChange={(event) => update('verifyToken', event.target.value)}
              />
              <button type="button" className="button button--ghost button--sm" onClick={handleGenerateToken}>
                <Icon name="refresh" />
                Generar
              </button>
              {values.verifyToken.trim().length > 0 ? (
                <button
                  type="button"
                  className="button button--ghost button--sm"
                  onClick={handleCopyToken}
                >
                  <Icon name={copiedToken ? 'check' : 'copy'} />
                  {copiedToken ? 'Copiado' : 'Copiar'}
                </button>
              ) : null}
            </div>
            {fieldErrors.verifyToken ? (
              <p className="field__error">{fieldErrors.verifyToken}</p>
            ) : null}
          </Field>

          <Field
            label="Version de la Graph API"
            htmlFor="account-graph-version"
            hint="Se usa para todas las llamadas a Meta. Deja v26.0 salvo que Meta retire esa version; entonces cambiala por la vigente, por ejemplo v27.0."
          >
            <input
              id="account-graph-version"
              className="input input--code"
              value={values.graphApiVersion}
              placeholder={DEFAULT_GRAPH_API_VERSION}
              onChange={(event) => update('graphApiVersion', event.target.value)}
            />
            {fieldErrors.graphApiVersion ? (
              <p className="field__error">{fieldErrors.graphApiVersion}</p>
            ) : null}
          </Field>

          {formError ? (
            <div className="banner banner--error" role="alert">
              <Icon name="error" className="banner__icon" />
              <p className="banner__text">{formError}</p>
            </div>
          ) : null}

          <div className="page__actions page__actions--end">
            <button
              type="button"
              className="button button--ghost"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button type="submit" className="button button--primary" disabled={!canWrite}>
              {submitting ? (
                <Spinner label="Guardando…" />
              ) : (
                <>
                  <Icon name="plus" />
                  Dar de alta el numero
                </>
              )}
            </button>
          </div>
        </form>
      </fieldset>
    </section>
  );
}

// --- Edicion de un numero ya dado de alta ------------------------------------

/** Etiquetas y explicaciones del estado activo/suspendido de un numero. */
const STATUS_OPTIONS: { value: AccountStatus; label: string; title: string }[] = [
  {
    value: 'active',
    label: ACCOUNT_STATUS_LABELS.active,
    title: 'El numero envia y recibe mensajes con normalidad.',
  },
  {
    value: 'disabled',
    label: ACCOUNT_STATUS_LABELS.disabled,
    title: 'Un numero suspendido no envia ni recibe mensajes.',
  },
];

const STATUS_HINTS: Record<AccountStatus, string> = {
  active: 'El numero envia y recibe mensajes con normalidad.',
  disabled:
    'Un numero suspendido no envia ni recibe mensajes: deja de entrar y salir trafico hasta que lo vuelvas a activar.',
};

/**
 * Valores iniciales de la edicion. Los campos no secretos se precargan con lo
 * que ya hay; los secretos nacen vacios porque la API nunca los devuelve, solo
 * dice si estan configurados (`hasAccessToken` y `hasAppSecret`).
 */
function initialEditValues(account: WhatsappAccountView): AccountFormValues {
  return {
    name: account.name,
    phoneNumberId: account.phoneNumberId,
    wabaId: account.wabaId,
    displayPhoneNumber: account.displayPhoneNumber ?? '',
    accessToken: '',
    appSecret: '',
    verifyToken: '',
    graphApiVersion: account.graphApiVersion,
  };
}

/**
 * Validacion de la edicion, espejo de la del backend pero con una diferencia
 * clave respecto al alta: los secretos pueden ir vacios (eso mantiene el valor
 * guardado), asi que solo se validan si traen valor.
 */
function validateEditForm(
  values: AccountFormValues,
  clear: { appSecret: boolean; verifyToken: boolean },
): AccountFieldErrors {
  const errors: AccountFieldErrors = {};

  const name =
    requiredText(values.name, 'El nombre interno') ??
    lengthText(values.name, 'El nombre interno', NAME_MIN, NAME_MAX);
  if (name) errors.name = name;

  const phoneNumberId =
    requiredText(values.phoneNumberId, 'El phone number ID') ??
    lengthText(values.phoneNumberId, 'El phone number ID', NUMBER_ID_MIN, NUMBER_ID_MAX);
  if (phoneNumberId) errors.phoneNumberId = phoneNumberId;

  const wabaId =
    requiredText(values.wabaId, 'El WABA ID') ??
    lengthText(values.wabaId, 'El WABA ID', NUMBER_ID_MIN, NUMBER_ID_MAX);
  if (wabaId) errors.wabaId = wabaId;

  if (values.displayPhoneNumber.trim().length > PHONE_MAX) {
    errors.displayPhoneNumber = `El numero visible no puede superar los ${PHONE_MAX} caracteres.`;
  }

  // Vacio significa "mantener el token actual", asi que no hay nada que validar.
  if (!isBlank(values.accessToken)) {
    const accessToken = lengthText(values.accessToken, 'El token de acceso', TOKEN_MIN, TOKEN_MAX);
    if (accessToken) errors.accessToken = accessToken;
  }

  if (!clear.appSecret && !isBlank(values.appSecret)) {
    if (values.appSecret.length < APP_SECRET_MIN || values.appSecret.length > APP_SECRET_MAX) {
      errors.appSecret = `El app secret debe tener entre ${APP_SECRET_MIN} y ${APP_SECRET_MAX} caracteres.`;
    }
  }

  if (!clear.verifyToken && !isBlank(values.verifyToken)) {
    const length = values.verifyToken.trim().length;
    if (length < VERIFY_TOKEN_MIN || length > VERIFY_TOKEN_MAX) {
      errors.verifyToken = `El token de verificacion debe tener entre ${VERIFY_TOKEN_MIN} y ${VERIFY_TOKEN_MAX} caracteres.`;
    }
  }

  if (!GRAPH_API_VERSION_PATTERN.test(values.graphApiVersion.trim())) {
    errors.graphApiVersion = 'La version debe tener el formato v26.0 (una «v», numeros y un punto).';
  }

  return errors;
}

/**
 * Cuerpo minimo del PATCH: solo viaja lo que ha cambiado respecto al numero
 * original, para no pisar datos sin querer. En los secretos se respetan las tres
 * convenciones del contrato: campo ausente (se mantiene), cadena (se reemplaza)
 * y `null` (se borra, solo al marcar la casilla correspondiente).
 */
function toUpdateAccountRequest(
  account: WhatsappAccountView,
  values: AccountFormValues,
  status: AccountStatus,
  clear: { appSecret: boolean; verifyToken: boolean },
): UpdateAccountRequest {
  const body: UpdateAccountRequest = {};

  const name = values.name.trim();
  if (name !== account.name) body.name = name;

  const phoneNumberId = values.phoneNumberId.trim();
  if (phoneNumberId !== account.phoneNumberId) body.phoneNumberId = phoneNumberId;

  const wabaId = values.wabaId.trim();
  if (wabaId !== account.wabaId) body.wabaId = wabaId;

  const displayPhoneNumber = values.displayPhoneNumber.trim();
  if (displayPhoneNumber !== (account.displayPhoneNumber ?? '')) {
    body.displayPhoneNumber = displayPhoneNumber;
  }

  const graphApiVersion = values.graphApiVersion.trim();
  if (graphApiVersion !== account.graphApiVersion) body.graphApiVersion = graphApiVersion;

  if (status !== account.status) body.status = status;

  // Un secreto vacio nunca viaja: dejarlo en blanco es mantener el actual.
  if (!isBlank(values.accessToken)) body.accessToken = values.accessToken;

  if (clear.appSecret) body.appSecret = null;
  else if (!isBlank(values.appSecret)) body.appSecret = values.appSecret.trim();

  if (clear.verifyToken) body.verifyToken = null;
  else if (!isBlank(values.verifyToken)) body.verifyToken = values.verifyToken.trim();

  return body;
}

/** Casilla que marca un secreto guardado para que el PATCH lo borre (`null`). */
function ClearSecretCheckbox({
  id,
  checked,
  disabled,
  label,
  onChange,
}: {
  id: string;
  checked: boolean;
  disabled: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}): ReactElement {
  return (
    <label className="checkline" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

/**
 * Formulario de edicion de un numero ya dado de alta.
 *
 * Reutiliza los patrones del alta (campos, validacion, errores por campo) y
 * anade lo propio de la edicion: los secretos no se pueden precargar, de modo
 * que cada uno muestra si hay valor guardado y permite mantenerlo, reemplazarlo
 * o borrarlo.
 */
function EditAccountForm({
  account,
  canWrite,
  onSaved,
  onCancel,
}: {
  account: WhatsappAccountView;
  canWrite: boolean;
  onSaved: (account: WhatsappAccountView) => void;
  onCancel: () => void;
}): ReactElement {
  const [values, setValues] = useState<AccountFormValues>(() => initialEditValues(account));
  const [status, setStatus] = useState<AccountStatus>(account.status);
  const [clearAppSecret, setClearAppSecret] = useState(false);
  const [clearVerifyToken, setClearVerifyToken] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<AccountFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [formInfo, setFormInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [showAppSecret, setShowAppSecret] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  const copyTimer = useRef<number | null>(null);
  const saveRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
      saveRef.current?.abort();
    },
    [],
  );

  const update = useCallback((field: AccountField, value: string): void => {
    setValues((prev) => ({ ...prev, [field]: value }));
    // Al corregir un campo se retira su aviso, para no dejar errores ya resueltos.
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleGenerateToken = useCallback((): void => {
    const token = generateVerifyToken();
    setClearVerifyToken(false);
    setValues((prev) => ({ ...prev, verifyToken: token }));
    setFieldErrors((prev) => {
      if (!prev.verifyToken) return prev;
      const next = { ...prev };
      delete next.verifyToken;
      return next;
    });
  }, []);

  const handleCopyToken = useCallback((): void => {
    void copyText(values.verifyToken).then((ok) => {
      if (!ok) return;
      setCopiedToken(true);
      if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopiedToken(false), 2400);
    });
  }, [values.verifyToken]);

  // Marcar la casilla vacia el campo: no se puede reemplazar y borrar a la vez.
  const handleClearAppSecret = useCallback((checked: boolean): void => {
    setClearAppSecret(checked);
    if (checked) {
      setValues((prev) => ({ ...prev, appSecret: '' }));
      setFieldErrors((prev) => {
        if (!prev.appSecret) return prev;
        const next = { ...prev };
        delete next.appSecret;
        return next;
      });
    }
  }, []);

  const handleClearVerifyToken = useCallback((checked: boolean): void => {
    setClearVerifyToken(checked);
    if (checked) {
      setValues((prev) => ({ ...prev, verifyToken: '' }));
      setFieldErrors((prev) => {
        if (!prev.verifyToken) return prev;
        const next = { ...prev };
        delete next.verifyToken;
        return next;
      });
    }
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      if (submitting) return;

      const clear = { appSecret: clearAppSecret, verifyToken: clearVerifyToken };
      const errors = validateEditForm(values, clear);
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setFormInfo(null);
        setFormError('Revisa los campos marcados.');
        return;
      }

      const body = toUpdateAccountRequest(account, values, status, clear);
      if (Object.keys(body).length === 0) {
        setFieldErrors({});
        setFormError(null);
        setFormInfo('No has cambiado nada, asi que no hay nada que guardar.');
        return;
      }

      setFieldErrors({});
      setFormError(null);
      setFormInfo(null);
      setSubmitting(true);

      const controller = new AbortController();
      saveRef.current?.abort();
      saveRef.current = controller;

      api
        .updateAccount(account.id, body, controller.signal)
        .then((updated) => {
          if (controller.signal.aborted) return;
          setSubmitting(false);
          onSaved(updated);
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted || isAbortError(error)) return;
          setSubmitting(false);
          if (error instanceof ApiError) {
            if (error.status === 404) {
              setFormError('Ese numero ya no existe. Cierra el formulario y actualiza la lista.');
              return;
            }
            if (error.status === 400 && error.invalidFields) {
              const parsed = invalidFieldErrors(error.invalidFields);
              setFieldErrors(parsed.fields);
              setFormError(parsed.general ?? 'El servidor ha rechazado algunos campos.');
              return;
            }
          }
          setFormError(errorMessage(error));
        });
    },
    [account, clearAppSecret, clearVerifyToken, onSaved, status, submitting, values],
  );

  return (
    <div className="diag">
      <div className="diag__head">
        <div>
          <p className="diag__title">Editar «{account.name}»</p>
          <p className="diag__hint">
            Corrige los datos del numero. Los secretos no se pueden mostrar: deja su campo vacio
            para mantener el que hay, escribe uno nuevo para reemplazarlo o marca la casilla para
            borrarlo. Al guardar se comprobara la conexion.
          </p>
        </div>
      </div>

      {!canWrite ? (
        <div className="banner banner--warning" role="status">
          <Icon name="warning" className="banner__icon" />
          <p className="banner__text">
            Solo el propietario y los administradores pueden cambiar la configuracion de un numero.
          </p>
        </div>
      ) : null}

      <fieldset className="fieldset" disabled={!canWrite || submitting}>
        <form onSubmit={handleSubmit} noValidate>
          <Field
            label="Nombre interno"
            htmlFor="edit-account-name"
            required
            hint="Solo para el panel: por ejemplo «Atencion al cliente» (1 a 120 caracteres)."
          >
            <input
              id="edit-account-name"
              className="input"
              maxLength={NAME_MAX}
              value={values.name}
              onChange={(event) => update('name', event.target.value)}
            />
            {fieldErrors.name ? <p className="field__error">{fieldErrors.name}</p> : null}
          </Field>

          <div className="grid grid--2">
            <Field
              label="Phone number ID"
              htmlFor="edit-account-phone-number-id"
              required
              hint="Meta for Developers → tu app → WhatsApp → Configuracion de la API: el «Identificador del numero de telefono» (5 a 64 caracteres)."
            >
              <input
                id="edit-account-phone-number-id"
                className="input input--code"
                maxLength={NUMBER_ID_MAX}
                value={values.phoneNumberId}
                onChange={(event) => update('phoneNumberId', event.target.value)}
              />
              {fieldErrors.phoneNumberId ? (
                <p className="field__error">{fieldErrors.phoneNumberId}</p>
              ) : null}
            </Field>

            <Field
              label="WABA ID"
              htmlFor="edit-account-waba-id"
              required
              hint="En la misma pantalla: el «Identificador de la cuenta de WhatsApp Business» de la que cuelga el numero (5 a 64 caracteres)."
            >
              <input
                id="edit-account-waba-id"
                className="input input--code"
                maxLength={NUMBER_ID_MAX}
                value={values.wabaId}
                onChange={(event) => update('wabaId', event.target.value)}
              />
              {fieldErrors.wabaId ? <p className="field__error">{fieldErrors.wabaId}</p> : null}
            </Field>
          </div>

          <Field
            label="Numero visible"
            htmlFor="edit-account-display-phone-number"
            hint="Como se ve el numero en WhatsApp, por ejemplo +34 600 000 000. Si lo dejas vacio, el panel lo pedira a Meta al comprobar la conexion (maximo 32 caracteres)."
          >
            <input
              id="edit-account-display-phone-number"
              className="input"
              maxLength={PHONE_MAX}
              value={values.displayPhoneNumber}
              placeholder="+34 600 000 000"
              onChange={(event) => update('displayPhoneNumber', event.target.value)}
            />
            {fieldErrors.displayPhoneNumber ? (
              <p className="field__error">{fieldErrors.displayPhoneNumber}</p>
            ) : null}
          </Field>

          <Field
            label="Token de acceso"
            htmlFor="edit-account-access-token"
            hint="Deja el campo vacio para mantener el token guardado, o escribe uno nuevo para reemplazarlo. Un numero sin token no puede enviar nada (20 a 2000 caracteres)."
          >
            <div className="editstate">
              <Chip tone={account.hasAccessToken ? 'success' : 'danger'}>
                {account.hasAccessToken ? 'Ya hay un token guardado' : 'Sin token guardado'}
              </Chip>
              <span className="editstate__text">
                {account.hasAccessToken
                  ? 'El token actual no se puede mostrar; se conserva salvo que escribas otro.'
                  : 'Este numero todavia no tiene token: es obligatorio para enviar mensajes.'}
              </span>
            </div>
            <div className="field-row">
              <input
                id="edit-account-access-token"
                className="input input--code"
                type={showAccessToken ? 'text' : 'password'}
                maxLength={TOKEN_MAX}
                value={values.accessToken}
                autoComplete="off"
                spellCheck={false}
                placeholder={account.hasAccessToken ? 'Escribe uno nuevo…' : 'EAA...'}
                onChange={(event) => update('accessToken', event.target.value)}
              />
              <button
                type="button"
                className="button button--ghost button--sm"
                aria-pressed={showAccessToken}
                onClick={() => setShowAccessToken((visible) => !visible)}
              >
                <Icon name={showAccessToken ? 'close' : 'search'} />
                {showAccessToken ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
            {fieldErrors.accessToken ? (
              <p className="field__error">{fieldErrors.accessToken}</p>
            ) : null}
          </Field>

          <Field
            label="App secret"
            htmlFor="edit-account-app-secret"
            hint="Sirve para comprobar la firma de los webhooks. Dejalo vacio para mantener el guardado o escribe uno nuevo para reemplazarlo (10 a 200 caracteres)."
          >
            <div className="editstate">
              <Chip tone={account.hasAppSecret ? 'success' : 'neutral'}>
                {account.hasAppSecret ? 'Ya hay un app secret guardado' : 'Sin app secret guardado'}
              </Chip>
              <span className="editstate__text">
                {account.hasAppSecret
                  ? 'El app secret actual no se puede mostrar.'
                  : 'Ahora mismo se usa el App Secret global de la aplicacion, si esta configurado.'}
              </span>
            </div>
            <div className="field-row">
              <input
                id="edit-account-app-secret"
                className="input input--code"
                type={showAppSecret ? 'text' : 'password'}
                maxLength={APP_SECRET_MAX}
                value={values.appSecret}
                autoComplete="off"
                spellCheck={false}
                disabled={clearAppSecret}
                placeholder={account.hasAppSecret ? 'Escribe uno nuevo…' : 'Clave secreta de la app'}
                onChange={(event) => update('appSecret', event.target.value)}
              />
              <button
                type="button"
                className="button button--ghost button--sm"
                aria-pressed={showAppSecret}
                disabled={clearAppSecret}
                onClick={() => setShowAppSecret((visible) => !visible)}
              >
                <Icon name={showAppSecret ? 'close' : 'search'} />
                {showAppSecret ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
            {account.hasAppSecret ? (
              <ClearSecretCheckbox
                id="edit-account-clear-app-secret"
                checked={clearAppSecret}
                disabled={submitting}
                label="Borrar el valor guardado: se volvera a usar el App Secret global de la aplicacion"
                onChange={handleClearAppSecret}
              />
            ) : null}
            {fieldErrors.appSecret ? <p className="field__error">{fieldErrors.appSecret}</p> : null}
          </Field>

          <Field
            label="Token de verificacion"
            htmlFor="edit-account-verify-token"
            hint="Lo eliges tu y sirve para que Meta confirme que el webhook es tuyo. Dejalo vacio para mantener el guardado, escribe uno nuevo para reemplazarlo o marcalo para borrarlo (6 a 200 caracteres)."
          >
            <div className="editstate">
              <Chip tone="neutral">No se puede mostrar</Chip>
              <span className="editstate__text">
                Si lo borras, Meta no podra verificar el webhook hasta que configures uno nuevo.
              </span>
            </div>
            <div className="field-row">
              <input
                id="edit-account-verify-token"
                className="input input--code"
                maxLength={VERIFY_TOKEN_MAX}
                value={values.verifyToken}
                autoComplete="off"
                spellCheck={false}
                disabled={clearVerifyToken}
                placeholder="Pulsa «Generar»"
                onChange={(event) => update('verifyToken', event.target.value)}
              />
              <button
                type="button"
                className="button button--ghost button--sm"
                disabled={clearVerifyToken}
                onClick={handleGenerateToken}
              >
                <Icon name="refresh" />
                Generar
              </button>
              {!clearVerifyToken && values.verifyToken.trim().length > 0 ? (
                <button
                  type="button"
                  className="button button--ghost button--sm"
                  onClick={handleCopyToken}
                >
                  <Icon name={copiedToken ? 'check' : 'copy'} />
                  {copiedToken ? 'Copiado' : 'Copiar'}
                </button>
              ) : null}
            </div>
            {/*
              La casilla solo tiene sentido si hay un token propio guardado: si no
              lo hay, la verificacion ya usa el global de la aplicacion.
            */}
            {account.hasVerifyToken ? (
              <ClearSecretCheckbox
                id="edit-account-clear-verify-token"
                checked={clearVerifyToken}
                disabled={submitting}
                label="Borrar el token de verificacion guardado de este numero"
                onChange={handleClearVerifyToken}
              />
            ) : (
              <p className="field__help">
                Este numero no tiene token de verificacion propio: se usa el global de la
                aplicacion. Escribe uno si quieres que use el suyo.
              </p>
            )}
            {fieldErrors.verifyToken ? (
              <p className="field__error">{fieldErrors.verifyToken}</p>
            ) : null}
          </Field>

          <Field
            label="Version de la Graph API"
            htmlFor="edit-account-graph-version"
            hint="Se usa para todas las llamadas a Meta. Cambiala solo si Meta retira la version actual, por ejemplo a v27.0."
          >
            <input
              id="edit-account-graph-version"
              className="input input--code"
              value={values.graphApiVersion}
              placeholder={DEFAULT_GRAPH_API_VERSION}
              onChange={(event) => update('graphApiVersion', event.target.value)}
            />
            {fieldErrors.graphApiVersion ? (
              <p className="field__error">{fieldErrors.graphApiVersion}</p>
            ) : null}
          </Field>

          <Field label="Estado del numero" hint={STATUS_HINTS[status]}>
            <SegmentedControl<AccountStatus>
              ariaLabel="Estado del numero"
              value={status}
              onChange={setStatus}
              options={STATUS_OPTIONS}
            />
          </Field>

          {formError ? (
            <div className="banner banner--error" role="alert">
              <Icon name="error" className="banner__icon" />
              <p className="banner__text">{formError}</p>
            </div>
          ) : null}

          {formInfo ? (
            <div className="banner banner--info" role="status">
              <Icon name="info" className="banner__icon" />
              <p className="banner__text">{formInfo}</p>
            </div>
          ) : null}

          <div className="page__actions page__actions--end">
            <button
              type="button"
              className="button button--ghost"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button type="submit" className="button button--primary" disabled={!canWrite}>
              {submitting ? (
                <Spinner label="Guardando…" />
              ) : (
                <>
                  <Icon name="check" />
                  Guardar cambios
                </>
              )}
            </button>
          </div>
        </form>
      </fieldset>
    </div>
  );
}

export function AccountsScreen({
  onNotice,
  canWrite,
}: {
  onNotice: (kind: NoticeKind, text: string) => void;
  /** Solo propietarios y administradores pueden dar de alta numeros (la API responde 403). */
  canWrite: boolean;
}): ReactElement {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  /** Formulario de alta desplegado. */
  const [dialogOpen, setDialogOpen] = useState(false);

  /** Solo se despliega la fila de una cuenta a la vez. */
  const [openId, setOpenId] = useState<string | null>(null);
  /** Contenido de la fila desplegada: diagnostico o formulario de edicion. */
  const [detailMode, setDetailMode] = useState<DetailMode>('diagnostic');
  const [verification, setVerification] = useState<Verification>({ status: 'running' });
  const [webhook, setWebhook] = useState<WebhookInfo>({ status: 'loading' });
  const runRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    api
      .listAccounts(controller.signal)
      .then((accounts) => setState({ status: 'ready', accounts }))
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setState({ status: 'error', message: errorMessage(error) });
      });
    return () => controller.abort();
  }, [reloadToken]);

  // Al desmontar la pantalla se cancela cualquier comprobacion en curso.
  useEffect(() => () => runRef.current?.abort(), []);

  const runVerification = useCallback((account: WhatsappAccountView): void => {
    // Al comprobar otro numero (o el mismo otra vez) se cancela lo anterior.
    runRef.current?.abort();
    const controller = new AbortController();
    runRef.current = controller;

    setOpenId(account.id);
    setDetailMode('diagnostic');
    setVerification({ status: 'running' });
    setWebhook({ status: 'loading' });

    api
      .verifyAccount(account.id, controller.signal)
      .then((diagnostic) => {
        if (controller.signal.aborted) return;
        setVerification({ status: 'ready', diagnostic });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setVerification({ status: 'error', message: errorMessage(error) });
      });

    api
      .getAccountWebhookInfo(account.id, controller.signal)
      .then((info) => {
        if (controller.signal.aborted) return;
        setWebhook({ status: 'ready', info });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setWebhook({ status: 'error', message: errorMessage(error) });
      });
  }, []);

  const closeDetail = useCallback((): void => {
    runRef.current?.abort();
    runRef.current = null;
    setOpenId(null);
  }, []);

  /**
   * El boton "Editar" reutiliza la fila desplegada del diagnostico: cancela
   * cualquier comprobacion en curso y muestra el formulario en su lugar.
   */
  const openEdit = useCallback((account: WhatsappAccountView): void => {
    runRef.current?.abort();
    runRef.current = null;
    setOpenId(account.id);
    setDetailMode('edit');
  }, []);

  /**
   * Alta correcta: se cierra el formulario, se recarga la lista (el numero nuevo
   * ya existe en el servidor, asi que se vuelve a pedir completa) y acto seguido
   * se lanza el diagnostico de ese numero para que el usuario vea al momento si
   * sus credenciales funcionan.
   */
  const handleCreated = useCallback(
    (account: WhatsappAccountView): void => {
      setDialogOpen(false);
      setReloadToken((token) => token + 1);
      onNotice('success', `Numero «${account.name}» dado de alta. Comprobando la conexion...`);
      runVerification(account);
    },
    [onNotice, runVerification],
  );

  /**
   * Edicion correcta: se cierra el formulario (la fila vuelve al diagnostico),
   * se recarga la lista y se comprueba la conexion con los datos nuevos, que es
   * justo lo que el usuario quiere ver tras corregir un token.
   */
  const handleSaved = useCallback(
    (account: WhatsappAccountView): void => {
      setReloadToken((token) => token + 1);
      onNotice('success', `Numero «${account.name}» actualizado. Comprobando la conexion...`);
      runVerification(account);
    },
    [onNotice, runVerification],
  );

  const openDialog = useCallback((): void => setDialogOpen(true), []);
  const closeDialog = useCallback((): void => setDialogOpen(false), []);

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1 className="page__title">Cuentas de WhatsApp</h1>
          <p className="page__subtitle">
            Numeros conectados por los que entra y sale el trafico de la organizacion activa.
          </p>
        </div>
        <div className="page__actions">
          <button
            type="button"
            className="button button--primary"
            onClick={openDialog}
            disabled={!canWrite || dialogOpen}
            title={
              canWrite
                ? undefined
                : 'Solo el propietario y los administradores pueden dar de alta numeros'
            }
          >
            <Icon name="plus" />
            Nuevo numero
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => setReloadToken((token) => token + 1)}
          >
            <Icon name="refresh" />
            Actualizar
          </button>
        </div>
      </header>

      {!canWrite ? (
        <div className="banner banner--warning" role="status">
          <Icon name="warning" className="banner__icon" />
          <p className="banner__text">
            Solo el propietario y los administradores pueden dar de alta o editar numeros de
            WhatsApp. Puedes consultar los numeros y comprobar su conexion, pero no cambiarlos.
          </p>
        </div>
      ) : null}

      {dialogOpen ? (
        <CreateAccountForm canWrite={canWrite} onCreated={handleCreated} onCancel={closeDialog} />
      ) : null}

      {state.status === 'loading' ? <SkeletonRows rows={4} /> : null}

      {state.status === 'error' ? (
        <ErrorBanner message={state.message} onRetry={() => setReloadToken((token) => token + 1)} />
      ) : null}

      {state.status === 'ready' && state.accounts.length === 0 ? (
        <EmptyState
          icon="accounts"
          title="No hay cuentas conectadas"
          description="Cuando se configure un numero de WhatsApp Business aparecera aqui, con su estado y su identificador."
          action={
            <button
              type="button"
              className="button button--primary"
              onClick={openDialog}
              disabled={!canWrite || dialogOpen}
              title={
                canWrite
                  ? undefined
                  : 'Solo el propietario y los administradores pueden dar de alta numeros'
              }
            >
              <Icon name="plus" />
              Anadir el primer numero
            </button>
          }
        />
      ) : null}

      {state.status === 'ready' && state.accounts.length > 0 ? (
        <div className="tablewrap">
          <table className="table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Numero</th>
                <th>Phone number ID</th>
                <th>WABA ID</th>
                <th>Estado</th>
                <th>Credenciales</th>
                <th>Version API</th>
                <th>Alta</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {state.accounts.map((account) => {
                const isOpen = openId === account.id;
                const isEditing = isOpen && detailMode === 'edit';
                const isRunning =
                  isOpen && detailMode === 'diagnostic' && verification.status === 'running';
                return (
                  <Fragment key={account.id}>
                    <tr>
                      <td className="table__strong">{account.name}</td>
                      <td>
                        {account.displayPhoneNumber
                          ? formatPhone(account.displayPhoneNumber)
                          : 'Sin numero publico'}
                      </td>
                      <td className="table__mono">{account.phoneNumberId}</td>
                      <td className="table__mono">{account.wabaId}</td>
                      <td>
                        <Chip tone={account.status === 'active' ? 'success' : 'warning'}>
                          {ACCOUNT_STATUS_LABELS[account.status]}
                        </Chip>
                      </td>
                      <td>
                        <span className="creds">
                          <Chip tone={account.hasAccessToken ? 'success' : 'danger'}>
                            {account.hasAccessToken ? 'Token configurado' : 'Sin token'}
                          </Chip>
                          <Chip tone={account.hasAppSecret ? 'success' : 'neutral'}>
                            {account.hasAppSecret ? 'App secret' : 'Sin app secret'}
                          </Chip>
                        </span>
                      </td>
                      <td className="table__mono">{account.graphApiVersion}</td>
                      <td>{formatDateTime(account.createdAt)}</td>
                      <td>
                        <div className="rowactions">
                          <button
                            type="button"
                            className="button button--sm"
                            onClick={() => runVerification(account)}
                            disabled={isRunning}
                          >
                            <Icon name="refresh" />
                            {isRunning ? 'Comprobando...' : 'Comprobar conexion'}
                          </button>
                          <button
                            type="button"
                            className="button button--sm"
                            onClick={() => openEdit(account)}
                            disabled={!canWrite || isEditing}
                            title={
                              canWrite
                                ? 'Corregir los datos y los secretos de este numero'
                                : 'Solo el propietario y los administradores pueden editar numeros'
                            }
                          >
                            <Icon name="edit" />
                            Editar
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr>
                        <td className="table__detail" colSpan={9}>
                          {isEditing ? (
                            <EditAccountForm
                              account={account}
                              canWrite={canWrite}
                              onSaved={handleSaved}
                              onCancel={closeDetail}
                            />
                          ) : (
                            <DiagnosticPanel
                              account={account}
                              verification={verification}
                              webhook={webhook}
                              onRetry={() => runVerification(account)}
                              onClose={closeDetail}
                            />
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
